import type { ApprovalChain, ApprovalChainStep } from '@/entities/approval-chain';

/** Who is in the project and which project roles they hold; one person may hold several. */
export interface MembershipSnapshot {
  memberIds: ReadonlySet<string>;
  holdsRole: (userId: string, roleId: string) => boolean;
}

export interface ApprovalStepShortfall {
  stepId: string;
  chainName: string;
  stepName: string;
  required: number;
  available: number;
}

/**
 * The approval steps a membership change leaves short of the approvals they need. A step
 * counts the people it names who stay in the project and the members holding its role, each
 * person once. A step already short before the change is listed only when the change leaves it
 * with fewer approvers still.
 */
export function approvalStepShortfalls(
  chains: readonly ApprovalChain[],
  before: MembershipSnapshot,
  after: MembershipSnapshot,
): ApprovalStepShortfall[] {
  return chains.flatMap((chain) =>
    (chain.steps ?? []).flatMap((step) => {
      const required = requiredApprovals(step);
      const available = approversAmong(step, after);
      if (available >= required || available >= approversAmong(step, before)) return [];
      return [{ stepId: step.id, chainName: chain.name, stepName: step.name, required, available }];
    }),
  );
}

function requiredApprovals(step: ApprovalChainStep): number {
  if (step.approvalThresholdType === 'all') return step.assignees.length;
  if (step.approvalThresholdType === 'at_least_one') return 1;
  // The generated client types the count loosely; the API sends a number.
  const count: unknown = step.approvalThresholdCount;
  return typeof count === 'number' ? count : 1;
}

function approversAmong(step: ApprovalChainStep, membership: MembershipSnapshot): number {
  const approvers = new Set(
    step.assignees
      .map((assignee) => assignee.userId)
      .filter((userId) => membership.memberIds.has(userId)),
  );
  const roleId = step.approverRole?.id;
  if (roleId) {
    for (const memberId of membership.memberIds) {
      if (membership.holdsRole(memberId, roleId)) approvers.add(memberId);
    }
  }
  return approvers.size;
}
